import { useEffect, useState, useRef } from "react";

interface StatItemProps {
  value: number;
  suffix: string;
  label: string;
  index: number;
}

function StatItem({ value, suffix, label, index }: StatItemProps) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const duration = 2000;
          const steps = 60;
          const increment = value / steps;
          let current = 0;

          const timer = setInterval(() => {
            current += increment;
            if (current >= value) {
              setCount(value);
              clearInterval(timer);
            } else {
              setCount(Math.floor(current));
            }
          }, duration / steps);
        }
      },
      { threshold: 0.5 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);

  return (
    <div
      ref={ref}
      className="group text-center p-6 rounded-2xl border border-transparent transition-all duration-500 hover:border-primary/10 hover:bg-primary/[0.02]"
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <div className="text-4xl md:text-5xl font-display font-bold text-gradient mb-2 transition-transform duration-300 group-hover:scale-105">
        {count.toLocaleString()}
        {suffix}
      </div>
      <div className="text-sm text-muted-foreground tracking-wide">{label}</div>
    </div>
  );
}

const stats = [
  { value: 10000, suffix: "+", label: "Requirements reviewed" },
  { value: 2500, suffix: "+", label: "Product teams" },
  { value: 40, suffix: "%", label: "Fewer clarification rounds" },
  { value: 8, suffix: "hrs", label: "Saved per sprint" },
];

export function StatsCounter() {
  return (
    <section className="py-16 md:py-20 relative overflow-hidden">
      {/* Subtle ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] rounded-full bg-primary/[0.04] blur-[120px]" />
      <div className="container relative z-10 px-4 md:px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
          {stats.map((stat, i) => (
            <StatItem
              key={stat.label}
              value={stat.value}
              suffix={stat.suffix}
              label={stat.label}
              index={i}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
